import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { getEnv } from "../lib/env";
import { buildInviteShareMessage } from "../lib/inviteShare";
import type { InviteShareKind } from "../lib/inviteShare";
import { isLiffShareTargetPickerAvailable, shareTextWithLiffTargetPicker } from "../lib/liff";

export function InviteSharePanel({
  tenantName,
  inviteKind,
  primaryUrl,
  webPreviewUrl,
  inviteId,
  onRevoke,
  revokeBusy = false
}: {
  tenantName: string;
  inviteKind: InviteShareKind;
  primaryUrl: string;
  webPreviewUrl?: string | null;
  inviteId?: string | null;
  onRevoke?: (() => void) | null;
  revokeBusy?: boolean;
}) {
  const env = getEnv();
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [shareAvailable, setShareAvailable] = useState(false);
  const [shareChecking, setShareChecking] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const inviteMessage = useMemo(
    () =>
      buildInviteShareMessage({
        tenantName,
        inviteKind,
        inviteUrl: primaryUrl
      }),
    [inviteKind, primaryUrl, tenantName]
  );
  const showWebPreviewUrl = Boolean(env.reviewDebugEnabled && webPreviewUrl && webPreviewUrl !== primaryUrl);

  useEffect(() => {
    if (!env.lineMiniAppLiffId) {
      setShareAvailable(false);
      return;
    }

    let cancelled = false;
    setShareChecking(true);

    void isLiffShareTargetPickerAvailable(env.lineMiniAppLiffId)
      .then((available) => {
        if (!cancelled) {
          setShareAvailable(available);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setShareChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [env.lineMiniAppLiffId]);

  useEffect(() => {
    let cancelled = false;

    void QRCode.toDataURL(primaryUrl, {
      width: 240,
      margin: 1
    })
      .then((value: string) => {
        if (!cancelled) {
          setQrCodeDataUrl(value);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQrCodeDataUrl(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [primaryUrl]);

  async function handleCopyUrl(targetUrl: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(targetUrl);
      setFeedbackMessage(successMessage);
    } catch {
      setFeedbackMessage("コピーに失敗しました。");
    }
  }

  async function handleCopyMessage() {
    try {
      await navigator.clipboard.writeText(inviteMessage);
      setFeedbackMessage("招待文をコピーしました。");
    } catch {
      setFeedbackMessage("招待文のコピーに失敗しました。");
    }
  }

  async function handleLineShare() {
    if (!env.lineMiniAppLiffId) {
      setFeedbackMessage("招待文をコピーしてLINEで送ってください。");
      return;
    }

    setShareBusy(true);
    setFeedbackMessage(null);

    try {
      await shareTextWithLiffTargetPicker(env.lineMiniAppLiffId, inviteMessage);
      setFeedbackMessage("LINEで共有しました。");
    } catch {
      setFeedbackMessage("招待文をコピーしてLINEで送ってください。");
    } finally {
      setShareBusy(false);
    }
  }

  return (
    <section
      style={{
        display: "grid",
        gap: 12,
        padding: 12,
        border: "1px solid #d8e8ff",
        borderRadius: 12,
        background: "#f8fbff"
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>招待を共有する</h2>
        {inviteId ? <div style={{ fontSize: 13, opacity: 0.75 }}>招待ID: {inviteId}</div> : null}
      </div>

      <label style={{ display: "grid", gap: 4 }}>
        <span>LINEアプリ用URL</span>
        <input readOnly value={primaryUrl} />
      </label>

      <div style={{ display: "grid", gap: 8 }}>
        <span>QRコード</span>
        <div
          style={{
            minHeight: 180,
            display: "grid",
            placeItems: "center",
            padding: 12,
            border: "1px dashed #b7c8dd",
            borderRadius: 12,
            background: "#ffffff"
          }}
        >
          {qrCodeDataUrl ? (
            <img
              src={qrCodeDataUrl}
              alt="招待URLのQRコード"
              width={180}
              height={180}
              style={{ width: 180, height: 180 }}
            />
          ) : (
            <div style={{ display: "grid", gap: 8, textAlign: "center", fontSize: 13, opacity: 0.8 }}>
              <strong style={{ fontSize: 16 }}>QRコードを準備しています</strong>
              <span>読み込みに失敗した場合は URL をコピーしてご利用ください。</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={() => void handleCopyUrl(primaryUrl, "URLをコピーしました。")}>
          URLをコピー
        </button>
        <button type="button" onClick={() => void handleCopyMessage()}>
          招待文をコピー
        </button>
        <button type="button" onClick={() => void handleLineShare()} disabled={shareBusy || shareChecking || !shareAvailable}>
          {shareBusy ? "送信中..." : "LINEで送る"}
        </button>
        {onRevoke ? (
          <button type="button" onClick={onRevoke} disabled={revokeBusy}>
            {revokeBusy ? "失効中..." : "失効する"}
          </button>
        ) : null}
      </div>

      {!shareChecking && !shareAvailable ? (
        <div style={{ fontSize: 13, opacity: 0.75 }}>
          招待文をコピーしてLINEで送ってください。
        </div>
      ) : null}

      {showWebPreviewUrl ? (
        <label style={{ display: "grid", gap: 4 }}>
          <span>Web確認用URL</span>
          <input readOnly value={webPreviewUrl ?? ""} />
        </label>
      ) : null}

      {feedbackMessage ? <div style={{ fontSize: 13 }}>{feedbackMessage}</div> : null}
    </section>
  );
}
